import React from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { Check, FileText, X, Info, AlertTriangle } from 'lucide-react';

// Mirror of the unit normalization in TimeImportPanel — kept small and
// local so the modal renders the right suffix without importing the
// whole batch panel.
const wageUnitLabel = (unit) => {
  if (!unit) return 'h';
  const u = String(unit).trim();
  if (u === 'Tim' || u === 'tim') return 'h';
  return u.toLowerCase();
};

// Wage codes that the user thinks of as "one concept" but get emitted
// as multiple rows by the classifier (e.g. semester sammalöneregeln
// always emits 3235 + 3220 + 3216 together; semestersersättning §28
// stands alone but is still "Semester" conceptually). Bundling them in
// the modal keeps the admin focused on what they're actually deciding —
// "import 7 days of vacation" — instead of three rows of the same
// economic event.
const SEMESTER_WAGE_CODES = new Set(['3216', '3220', '3221', '3232', '3235']);

const groupModalRows = (rows) => {
  if (!rows?.length) return [];
  const semester = rows.filter(r => SEMESTER_WAGE_CODES.has(r.wageTypeCode));
  const others = rows.filter(r => !SEMESTER_WAGE_CODES.has(r.wageTypeCode));
  const groups = [];
  if (semester.length > 0) {
    groups.push({
      kind: 'semester',
      label: 'Semester',
      quantity: semester[0].quantity,
      unit: semester[0].wageTypeUnit,
      wageCodes: semester.map(r => r.wageTypeCode),
    });
  }
  for (const r of others) {
    groups.push({
      kind: 'single',
      label: r.wageTypeName,
      wageCode: r.wageTypeCode,
      quantity: r.quantity,
      unit: r.wageTypeUnit,
    });
  }
  return groups;
};

/**
 * PayslipTimeImportModal — focused single-payslip import flow.
 *
 * The batch payroll-level flow (many employees in one view) keeps using
 * TimeImportPanel inline; this modal is the single-payslip equivalent
 * where the admin is already inside one payslip and just wants to
 * commit "import these rows / cancel".
 *
 * Visual frame mirrors SelectWageTypeModal (modern-wage-modal class +
 * info-section banners + modern-btn footer) so the two add-rows entry
 * points feel like the same product.
 */
const PayslipTimeImportModal = ({
  show,
  previewData,
  loading,
  error,
  importing,
  onClose,
  onConfirmImport,
}) => {
  const employee = previewData?.employees?.[0];
  const rows = employee?.rows || [];
  const groups = groupModalRows(rows);
  const warnings = previewData?.warnings || [];
  const adjustmentPeriod = employee?.adjustmentPeriod;

  const handleConfirm = () => {
    if (!employee?.employeeId) return;
    onConfirmImport({ employeeIds: [employee.employeeId] });
  };

  const totalsLine = (() => {
    if (!employee) return null;
    const itemLabel = `${groups.length} ${groups.length === 1 ? 'post' : 'poster'}`;
    if (employee.absenceHours > 0) {
      return `${employee.workedHours}h arbete + ${employee.absenceHours}h frånvaro · ${itemLabel}`;
    }
    return `${employee.totalHours}h · ${itemLabel}`;
  })();

  return (
    <>
      <style>{`
        .modern-wage-modal .modal-content {
          border: none;
          border-radius: 16px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }
        .modern-wage-modal .modal-header {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border-bottom: 1px solid #e5e7eb;
          border-radius: 16px 16px 0 0;
          padding: 2rem 2rem 1.5rem 2rem;
        }
        .modern-wage-modal .modal-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.5rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }
        .modern-wage-modal .modal-body {
          padding: 2rem;
          background: #f8fafc;
          max-height: 70vh;
          overflow-y: auto;
        }
        .modern-wage-modal .modal-footer {
          background: white;
          border-top: 1px solid #e5e7eb;
          border-radius: 0 0 16px 16px;
          padding: 1.5rem 2rem;
        }

        .ti-info-section {
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          border: 1px solid #86efac;
          border-radius: 12px;
          padding: 1.25rem 1.5rem;
          margin-bottom: 1.5rem;
          display: flex;
          gap: 1rem;
          align-items: flex-start;
        }
        .ti-info-section.warning {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border-color: #fcd34d;
        }
        .ti-info-section.error {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          border-color: #fca5a5;
        }
        .ti-info-icon {
          color: var(--bs-primary);
          flex-shrink: 0;
        }
        .ti-info-section.warning .ti-info-icon { color: #b45309; }
        .ti-info-section.error .ti-info-icon { color: #b91c1c; }
        .ti-info-text {
          color: #166534;
          font-size: 0.875rem;
          line-height: 1.6;
          flex: 1;
        }
        .ti-info-section.warning .ti-info-text { color: #78350f; }
        .ti-info-section.error .ti-info-text { color: #7f1d1d; }
        .ti-info-text strong { font-weight: 600; }

        .ti-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 1rem 1.25rem;
          margin-bottom: 1rem;
        }

        .ti-rows-table {
          width: 100%;
          border-collapse: collapse;
        }
        .ti-rows-table th {
          text-align: left;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
          padding: 0.5rem 0.75rem;
          border-bottom: 1px solid #e5e7eb;
        }
        .ti-rows-table th.num { text-align: right; }
        .ti-rows-table td {
          padding: 0.75rem;
          border-bottom: 1px solid #f1f5f9;
          font-size: 0.875rem;
          color: #1e293b;
          vertical-align: top;
        }
        .ti-rows-table tr:last-child td { border-bottom: none; }
        .ti-rows-table td.num {
          text-align: right;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .ti-rows-table td.muted {
          color: #64748b;
          font-size: 0.8125rem;
        }
        .ti-row-sub {
          font-size: 0.75rem;
          color: #64748b;
          margin-top: 0.25rem;
        }

        .ti-empty {
          background: white;
          border: 1px dashed #d1d5db;
          border-radius: 12px;
          padding: 3rem 2rem;
          text-align: center;
          color: #64748b;
        }
        .ti-empty h4 {
          margin: 1rem 0 0.5rem 0;
          color: #374151;
          font-size: 1.125rem;
          font-weight: 600;
        }
        .ti-empty p {
          margin: 0;
          font-size: 0.875rem;
          line-height: 1.5;
        }
        .ti-empty-icon { color: #9ca3af; }

        .ti-loading {
          text-align: center;
          padding: 3rem 0;
          color: #64748b;
        }

        .modern-btn {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          min-width: 120px;
          justify-content: center;
        }
        .btn-secondary-modern {
          background: #f1f5f9;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        .btn-secondary-modern:hover {
          background: #e2e8f0;
          color: #475569;
        }
        .btn-primary-modern {
          background: var(--bs-primary);
          color: white;
        }
        .btn-primary-modern:hover:not(:disabled) {
          background: var(--primary-hover);
          transform: translateY(-1px);
        }
        .btn-primary-modern:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        @media (max-width: 768px) {
          .modern-wage-modal .modal-header,
          .modern-wage-modal .modal-body,
          .modern-wage-modal .modal-footer {
            padding: 1.5rem 1rem;
          }
          .modern-btn { width: 100%; }
        }
      `}</style>

      <Modal show={show} onHide={onClose} size="lg" className="modern-wage-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <FileText size={24} color="var(--bs-primary)" />
            Importera tidrapport{employee ? ` — ${employee.firstname} ${employee.lastname}` : ''}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading && (
            <div className="ti-loading">
              <Spinner animation="border" size="sm" /> Laddar tidrapport…
            </div>
          )}

          {error && (
            <div className="ti-info-section error">
              <AlertTriangle size={20} className="ti-info-icon" />
              <div className="ti-info-text">{error}</div>
            </div>
          )}

          {warnings.length > 0 && (
            <div className="ti-info-section warning">
              <AlertTriangle size={20} className="ti-info-icon" />
              <div className="ti-info-text">
                <strong>Varningar ({warnings.length})</strong>
                {warnings.map((w, i) => (
                  <div key={i} style={{ marginTop: 4 }}>• {w}</div>
                ))}
              </div>
            </div>
          )}

          {!loading && employee && (adjustmentPeriod || totalsLine) && (
            <div className="ti-info-section">
              <Info size={20} className="ti-info-icon" />
              <div className="ti-info-text">
                {adjustmentPeriod && (
                  <div>
                    Period: <strong>{adjustmentPeriod.from}</strong> — <strong>{adjustmentPeriod.to}</strong>
                  </div>
                )}
                {totalsLine && <div>{totalsLine}</div>}
              </div>
            </div>
          )}

          {!loading && employee && rows.length > 0 && (
            <div className="ti-card">
              <table className="ti-rows-table">
                <thead>
                  <tr>
                    <th>Typ</th>
                    <th className="num">Antal</th>
                    <th>Belopp</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group, gi) => (
                    <tr key={gi}>
                      <td>
                        {group.kind === 'semester' ? (
                          <>
                            {group.label}
                            <div className="ti-row-sub">
                              Genererar lönerader: {group.wageCodes.join(', ')}
                            </div>
                          </>
                        ) : (
                          <>
                            {group.label}{' '}
                            <span className="ti-row-sub" style={{ display: 'inline' }}>
                              ({group.wageCode})
                            </span>
                          </>
                        )}
                      </td>
                      <td className="num">
                        {group.quantity} {wageUnitLabel(group.unit)}
                      </td>
                      <td className="muted">Lön räknas ut från anställningsavtalet</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && employee && rows.length === 0 && (
            <div className="ti-empty">
              <FileText size={48} className="ti-empty-icon" />
              <h4>Inga tidposter att importera</h4>
              <p>
                Det finns inga godkända, oimporterade tidposter för perioden.
                Se till att den anställdes tidrapport är godkänd först.
              </p>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-between w-100 align-items-center">
            <small className="text-muted">
              💡 Importerar alla godkända tidposter i ett steg
            </small>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="modern-btn btn-secondary-modern"
                onClick={onClose}
                disabled={importing}
              >
                <X size={16} />
                Avbryt
              </button>
              <button
                type="button"
                className="modern-btn btn-primary-modern"
                onClick={handleConfirm}
                disabled={importing || loading || rows.length === 0}
              >
                {importing ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <Check size={16} />
                    Lägg till {groups.length} {groups.length === 1 ? 'post' : 'poster'}
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PayslipTimeImportModal;
